/* ============================================================================
 * pattern-email-unlock.js
 *
 * Klaviyo email-capture gate for free pattern downloads (replaces the old
 * account/login requirement). A long-lived cookie remembers that a visitor has
 * given their email so the form is not shown again.
 *
 * Works with:
 *   - snippets/pattern-login.liquid     (modal: embed + spinner + fallback)
 *   - sections/product-template.liquid  (button: [data-pattern-button][data-pdf-url])
 *
 * Cookies (read path treats EITHER as "unlocked"):
 *   - lb_pattern_email = 1        365 days. Set on a real email capture (Klaviyo
 *                                 form submit).
 *   - lb_pattern_email_temp = 1   3 days. Set when the visitor uses the fallback
 *                                 "Continue" button (Klaviyo blocked/failed, so
 *                                 no email captured). Short pass so they can grab
 *                                 patterns that day without re-hitting the form.
 *
 * Modal states (Klaviyo renders its embed at PAGE LOAD, so normally the form is
 * already present when the modal opens):
 *   - embed has children on open  -> show form (normal path, no spinner)
 *   - embed empty on open         -> show spinner, wait GRACE_MS for a late
 *                                    render, then show fallback if still empty
 *
 * Fallback: a "Continue" button that sets the temp pass, rewires the download
 * button to a direct PDF link, and closes the modal (so the next click on the
 * page's "Download Free Pattern" button downloads directly).
 *
 * Button rewiring: once unlocked, the "Download Free Pattern" button is turned
 * into a direct PDF link (like the logged-in experience). Resilient to render
 * timing via load rewire + MutationObserver + a capture-phase click backstop.
 *
 * Modal close after submit: no timer. Closes on the X (theme native) or on
 * Klaviyo's success button (Klaviyo `close` event, or post-submit click in form).
 * ========================================================================== */
(function () {
  'use strict';

  // --- Config ----------------------------------------------------------------
  var COOKIE_NAME = 'lb_pattern_email';       // durable, real signups (365d)
  var TEMP_COOKIE_NAME = 'lb_pattern_email_temp'; // fallback link users (1d)
  var COOKIE_DAYS = 365;
  var TEMP_COOKIE_DAYS = 3;

  // 'site' = one cookie unlocks all patterns. 'product' = a cookie per product.
  var SCOPE = 'site';

  // Suffix of "klaviyo-form-X5zD5n". Only this form sets the durable cookie.
  var PATTERN_FORM_ID = 'X5zD5n';

  // Grace period (ms) to wait for a late Klaviyo render when the embed is EMPTY
  // at modal-open, before deciding it's blocked and showing the fallback. Only
  // ever applies to the empty-at-open case; the normal (already-rendered) path
  // is instant. Tune here.
  var GRACE_MS = 2000;

  var BTN_SELECTOR = '[data-pattern-button][data-action="open-modal"]';
  var EMBED_SELECTOR = '#pattern-login [class^="klaviyo-form-"]';

  // --- Cookie helpers --------------------------------------------------------
  function keyFor(base) {
    if (SCOPE === 'product') {
      var btn = document.querySelector('[data-pattern-button]');
      var handle =
        (btn && btn.getAttribute('data-product-handle')) ||
        window.location.pathname.replace(/[^a-z0-9]+/gi, '_');
      return base + '__' + handle;
    }
    return base;
  }

  function setCookie(name, value, days) {
    var expires = '';
    if (days) {
      var d = new Date();
      d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + d.toUTCString();
    }
    document.cookie =
      name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
  }

  function getCookie(name) {
    var parts = document.cookie ? document.cookie.split('; ') : [];
    for (var i = 0; i < parts.length; i++) {
      var idx = parts[i].indexOf('=');
      if (idx > -1 && parts[i].slice(0, idx) === name) {
        return decodeURIComponent(parts[i].slice(idx + 1));
      }
    }
    return null;
  }

  // Unlocked if EITHER the durable or the temp pass is present.
  function hasUnlocked() {
    return (
      getCookie(keyFor(COOKIE_NAME)) === '1' ||
      getCookie(keyFor(TEMP_COOKIE_NAME)) === '1'
    );
  }

  function setDurablePass() { setCookie(keyFor(COOKIE_NAME), '1', COOKIE_DAYS); }
  function setTempPass() { setCookie(keyFor(TEMP_COOKIE_NAME), '1', TEMP_COOKIE_DAYS); }

  // --- Button rewiring -------------------------------------------------------
  function rewireButton(btn) {
    if (!btn || btn.getAttribute('data-pattern-rewired') === '1') return;
    var url = btn.getAttribute('data-pdf-url');
    if (!url) return;

    var link = document.createElement('a');
    link.className = btn.className;
    link.href = url;
    link.target = '_blank';
    link.rel = 'noreferrer';
    link.setAttribute('data-pattern-button', '');
    link.setAttribute('data-pattern-rewired', '1');
    link.innerHTML = btn.innerHTML;
    btn.parentNode.replaceChild(link, btn);
  }

  function rewireAllButtons() {
    var buttons = document.querySelectorAll(BTN_SELECTOR);
    for (var i = 0; i < buttons.length; i++) rewireButton(buttons[i]);
  }

  // Observe for pattern buttons that appear/re-render after load, and rewire
  // them. Single-instance and self-disconnecting: once no un-rewired button
  // remains, it stops observing (avoids a perpetual document-wide observer).
  var buttonObserver = null;

  function startButtonObserver() {
    if (buttonObserver || !('MutationObserver' in window)) return; // one instance only
    buttonObserver = new MutationObserver(function () {
      if (document.querySelector(BTN_SELECTOR)) {
        rewireAllButtons();
      }
      // Nothing left to rewire -> stop watching.
      if (!document.querySelector(BTN_SELECTOR)) {
        buttonObserver.disconnect();
        buttonObserver = null;
      }
    });
    buttonObserver.observe(document.body || document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  // --- Modal state helpers ---------------------------------------------------
  function el(sel, root) { return (root || document).querySelector(sel); }

  function embedHasForm() {
    var embed = el(EMBED_SELECTOR);
    return !!(embed && embed.children.length > 0);
  }

  function showState(which) {
    var email = el('.PatternEmailModal__Email');
    var spinner = el('[data-pattern-spinner]');
    var fallback = el('[data-pattern-fallback]');
    if (email) email.hidden = which !== 'form';
    if (spinner) spinner.hidden = which !== 'spinner';
    if (fallback) fallback.hidden = which !== 'fallback';

    // When the fallback appears, move focus to it so keyboard and screen-reader
    // users are taken to the new content and its message is announced.
    if (which === 'fallback' && fallback) {
      // focus after the hidden attribute is removed / paint
      requestAnimationFrame(function () {
        try { fallback.focus(); } catch (e) {}
      });
    }
  }

  function modalIsOpen() {
    var modal = document.getElementById('pattern-login');
    return !!modal && modal.getAttribute('aria-hidden') === 'false';
  }

  // --- Modal close -----------------------------------------------------------
  function closeThemeModal() {
    var modal = document.getElementById('pattern-login');
    if (!modal || modal.getAttribute('aria-hidden') === 'true') return;
    var closeBtn = modal.querySelector('[data-action="close-modal"]');
    if (closeBtn) closeBtn.click();
  }

  // --- Modal open handling ---------------------------------------------------
  var graceTimer = null;

  function onModalOpen() {
    // Clear any prior grace timer from a previous open.
    if (graceTimer) { clearTimeout(graceTimer); graceTimer = null; }

    if (embedHasForm()) {
      showState('form');          // normal path: form already rendered at load
      return;
    }

    // Without marketing consent Klaviyo never loads, so there's nothing to wait for.
    if (!document.documentElement.classList.contains('klaviyo-allowed')) {
      showState('fallback');
      return;
    }

    // Empty at open: could be a slow cold render, or blocked. Show spinner and
    // give it a short grace period before declaring it blocked.
    showState('spinner');

    var start = Date.now();
    (function poll() {
      if (!modalIsOpen()) { showState('form'); return; } // closed during wait; reset
      if (embedHasForm()) { showState('form'); return; }  // rendered late; show it
      if (Date.now() - start >= GRACE_MS) { showState('fallback'); return; }
      graceTimer = setTimeout(poll, 150);
    })();
  }

  // Detect the modal opening. The theme flips aria-hidden to "false" on open.
  function watchModalOpen() {
    var modal = document.getElementById('pattern-login');
    if (!modal || !('MutationObserver' in window)) return;
    new MutationObserver(function () {
      if (modal.getAttribute('aria-hidden') === 'false') {
        onModalOpen();
      } else {
        // On close: if focus is still inside the modal (e.g. a Klaviyo input),
        // blur it. A focused descendant blocks aria-hidden on the modal, which
        // breaks the theme's hide contract and leaves the modal's content laid
        // over the page. Blurring releases that so the modal can hide cleanly.
        var active = document.activeElement;
        if (active && modal.contains(active) && typeof active.blur === 'function') {
          active.blur();
        }
      }
    }).observe(modal, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  // --- Init ------------------------------------------------------------------
  function init() {
    var submittedThisSession = false;

    // If Klaviyo already knows this visitor's email (identified profile), we
    // won't make them sign up again. isIdentified() may be a promise on the
    // modern klaviyo object, so resolve it once on load into a flag the click
    // backstop below reads synchronously. This does NOT rewire the button or
    // change anything on load — an identified visitor's CLICK simply bypasses
    // the modal and goes straight to the PDF (same path as an unlocked cookie).
    var klaviyoIdentified = false;
    try {
      if (window.klaviyo && typeof klaviyo.isIdentified === 'function') {
        Promise.resolve(klaviyo.isIdentified())
          .then(function (v) { klaviyoIdentified = v === true; })
          .catch(function () {});
      }
    } catch (e) {}

    // If already unlocked, rewire buttons now and keep rewiring late ones.
    if (hasUnlocked()) {
      rewireAllButtons();
      startButtonObserver();
    }

    // Capture-phase backstop: if the visitor is unlocked (cookie) OR already
    // identified by Klaviyo, a click on a pattern button goes straight to the
    // PDF instead of opening the modal.
    document.addEventListener(
      'click',
      function (e) {
        if (!hasUnlocked() && !klaviyoIdentified) return;
        var t = e.target;
        if (!t || !t.closest) return;
        var btn = t.closest(BTN_SELECTOR);
        if (!btn) return;
        var url = btn.getAttribute('data-pdf-url');
        if (!url) return;
        e.preventDefault();
        e.stopPropagation();
        window.open(url, '_blank', 'noopener');
      },
      true
    );

    // Fallback "Continue" button -> set 3-day temp pass (no email captured),
    // rewire the pattern button so the visitor's next click downloads directly,
    // then close the modal.
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('[data-pattern-fallback-continue]')) {
        setTempPass();
        rewireAllButtons();
        startButtonObserver();
        closeThemeModal();
      }
    });

    // Watch for the modal opening to drive the spinner/fallback states.
    watchModalOpen();

    // Klaviyo onsite events: `submit` (success), `close` (form dismissed).
    window.addEventListener('klaviyoForms', function (e) {
      if (!e || !e.detail) return;
      if (PATTERN_FORM_ID && e.detail.formId && e.detail.formId !== PATTERN_FORM_ID) return;

      if (e.detail.type === 'submit') {
        submittedThisSession = true;
        setDurablePass();
        rewireAllButtons();
        startButtonObserver();
        return;
      }
      if (e.detail.type === 'close' && submittedThisSession) {
        closeThemeModal();
      }
    });

    // Fallback for the success button if it doesn't fire Klaviyo's `close`.
    document.addEventListener('click', function (e) {
      if (!submittedThisSession) return;
      var t = e.target;
      if (!t || !t.closest) return;
      if (!t.closest('#pattern-login [class^="klaviyo-form-"]')) return;
      if (t.closest('button, a, [role="button"]')) {
        setTimeout(closeThemeModal, 0);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
